import { FactoryIcon, WarningIcon } from '@phosphor-icons/react';
import { useEffect, useState, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';
import { loginRequest } from '@/services/auth';

interface LocationState {
  readonly from?: string;
}

/**
 * Plan 06-03 LoginForm — replaces the Phase 3 LoginStub.
 *
 * Submits `{ identifier, password, rememberMe }` to `POST /api/auth/login` (the auth API
 * shipped in Plan 06-02). On 200, persists tokens + user + permissions via AuthContext
 * and routes to `/dashboard` (or `/force-change-password` when the user is force-flagged).
 *
 * Error handling:
 *  - 401 → "Invalid credentials" alert + toast
 *  - 423 → countdown banner reading the `Retry-After` seconds (parsed via ApiError.retryAfter)
 *  - 400 → field-level Zod errors on the client; backend detail surfaced in alert
 *  - other → generic "Login failed" / "Network error" message
 */

const loginSchema = z.object({
  identifier: z.string().min(3, 'Identifier is required (email or employee ID)'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.input<typeof loginSchema>;

function formatCountdown(seconds: number): string {
  const safe = Math.max(0, seconds);
  const mm = Math.floor(safe / 60);
  const ss = safe % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

export function LoginForm(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lockedSecondsRemaining, setLockedSecondsRemaining] = useState<number | null>(null);
  const [authMessage, setAuthMessage] = useState<string | null>(null);

  const form = useZodForm({
    schema: loginSchema,
    defaultValues: { identifier: '', password: '', rememberMe: false },
  });

  // Pick up any one-shot reason set by the axios interceptor (e.g., "Your session has expired").
  useEffect(() => {
    try {
      const msg = window.sessionStorage.getItem('aryan:auth-message');
      if (msg !== null) {
        setAuthMessage(msg);
        window.sessionStorage.removeItem('aryan:auth-message');
      }
    } catch {
      // Ignore storage errors.
    }
  }, []);

  // Countdown ticker — decrements every second while we have a remaining lock duration.
  useEffect(() => {
    if (lockedSecondsRemaining === null || lockedSecondsRemaining <= 0) return undefined;
    const t = window.setTimeout(() => {
      setLockedSecondsRemaining((s) => (s === null ? null : Math.max(0, s - 1)));
    }, 1000);
    return () => {
      window.clearTimeout(t);
    };
  }, [lockedSecondsRemaining]);

  async function onSubmit(values: LoginFormValues): Promise<void> {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const response = await loginRequest({
        identifier: values.identifier,
        password: values.password,
        rememberMe: values.rememberMe ?? false,
      });
      login(response.accessToken, response.refreshToken, response.user, response.permissions);
      const fallbackDest = (location.state as LocationState | null)?.from ?? '/dashboard';
      const dest = response.user.passwordChangeRequired ? '/force-change-password' : fallbackDest;
      toast.success(`Welcome, ${response.user.fullName}`);
      void navigate(dest, { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 423) {
          const seconds = e.retryAfter ?? 1800;
          setLockedSecondsRemaining(seconds);
          setErrorMessage('Account is locked. Try again later.');
        } else if (e.status === 401) {
          setErrorMessage('Invalid credentials.');
        } else if (e.status === 400) {
          setErrorMessage(e.detail ?? 'Validation failed.');
        } else if (e.status === 0) {
          setErrorMessage('Network error — please try again.');
        } else {
          setErrorMessage(e.detail ?? 'Login failed.');
        }
        toast.error('Login failed', { description: e.detail ?? e.message });
      } else {
        setErrorMessage('Network error — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-gradient-to-br from-primary/10 via-background to-accent/10 min-h-screen flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center space-y-3">
          <div className="h-16 w-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mx-auto">
            <FactoryIcon size={32} weight="bold" />
          </div>
          <CardTitle className="text-xl">Aryan O&amp;M v2</CardTitle>
          <CardDescription>Sign in with your employee ID or email.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {authMessage !== null && (
            <div
              role="status"
              className="rounded-md border border-warning/50 bg-warning/10 p-2 text-xs text-warning-foreground"
            >
              {authMessage}
            </div>
          )}

          {errorMessage !== null && (
            <div
              role="alert"
              data-testid="login-error"
              className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2"
            >
              <WarningIcon size={16} weight="fill" className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <div>{errorMessage}</div>
                {lockedSecondsRemaining !== null && lockedSecondsRemaining > 0 && (
                  <div className="mt-1 font-mono text-xs" data-testid="login-lock-countdown">
                    Try again in {formatCountdown(lockedSecondsRemaining)}
                  </div>
                )}
              </div>
            </div>
          )}

          <form
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit)(e);
            }}
            className="space-y-4"
            noValidate
          >
            <div className="space-y-2">
              <Label htmlFor="identifier">Email or Employee ID</Label>
              <Input
                id="identifier"
                type="text"
                autoComplete="username"
                disabled={submitting}
                aria-invalid={form.formState.errors.identifier !== undefined}
                {...form.register('identifier')}
                data-testid="login-identifier"
              />
              {form.formState.errors.identifier && (
                <p className="text-xs text-destructive" data-testid="login-identifier-error">
                  {form.formState.errors.identifier.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                disabled={submitting}
                aria-invalid={form.formState.errors.password !== undefined}
                {...form.register('password')}
                data-testid="login-password"
              />
              {form.formState.errors.password && (
                <p className="text-xs text-destructive" data-testid="login-password-error">
                  {form.formState.errors.password.message}
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                {...form.register('rememberMe')}
                disabled={submitting}
                data-testid="login-remember-me"
                className="h-4 w-4 rounded border-input"
              />
              <span>Remember me for 30 days</span>
            </label>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="login-submit"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
